import {
  Box,
  Button,
  Container,
  Grid,
  Stack,
  Typography,
  useTheme,
  alpha,
  Card,
} from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PersonAdd,
  Search,
  Person,
  Assignment,
  School,
  ArrowForward,
  CheckCircle,
  Verified,
  Business as BusinessIcon,
  Group,
} from '@mui/icons-material';

function Business() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const steps = [
    {
      icon: <PersonAdd sx={{ fontSize: 48 }} />,
      step: '01',
      title: t('business.step1Title') || 'Create Your Business Account',
      desc: t('business.step1Desc') || 'Sign up as a business and get access to employee management tools.',
    },
    {
      icon: <Group sx={{ fontSize: 48 }} />,
      step: '02',
      title: t('business.step2Title') || 'Add Your Employees',
      desc: t('business.step2Desc') || 'Invite employees by email or create accounts for employees without email addresses.',
    },
    {
      icon: <Search sx={{ fontSize: 48 }} />,
      step: '03',
      title: t('business.step3Title') || 'Find Expert Tutors',
      desc: t('business.step3Desc') || 'Browse and select from our verified pool of expert tutors.',
    },
    {
      icon: <Assignment sx={{ fontSize: 48 }} />,
      step: '04',
      title: t('business.step4Title') || 'Request Tutoring Sessions',
      desc: t('business.step4Desc') || 'Request tutoring sessions for one or more of your employees.',
    },
    {
      icon: <School sx={{ fontSize: 48 }} />,
      step: '05',
      title: t('business.step5Title') || 'Track Progress',
      desc: t('business.step5Desc') || 'Monitor combined progress across all employees and individual performance.',
    },
  ];

  const benefits = [
    {
      icon: <Verified sx={{ fontSize: 40 }} />,
      title: t('business.benefit1Title') || 'Centralized Management',
      desc: t('business.benefit1Desc') || 'Manage all your employees\' learning in one place.',
    },
    {
      icon: <CheckCircle sx={{ fontSize: 40 }} />,
      title: t('business.benefit2Title') || 'Flexible Access',
      desc: t('business.benefit2Desc') || 'Employees can access tutoring with or without email addresses.',
    },
    {
      icon: <BusinessIcon sx={{ fontSize: 40 }} />,
      title: t('business.benefit3Title') || 'Bulk Requests',
      desc: t('business.benefit3Desc') || 'Request tutoring sessions for multiple employees at once.',
    },
  ];

  return (
    <Box sx={{ mt: '-44px' }}>
      {/* Hero Section */}
      <Box
        sx={{
          minHeight: '60vh',
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                  fontWeight: 800,
                  color: 'white',
                  mb: 3,
                  lineHeight: 1.2,
                }}
              >
                {t('business.heroTitle') || 'Empower Your Team with Quality Education'}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: alpha('#ffffff', 0.95),
                  mb: 4,
                  lineHeight: 1.6,
                }}
              >
                {t('business.heroSubtitle') || 'Provide your employees with access to expert tutors and track their learning progress all in one platform.'}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/signup/business')}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    borderRadius: 2,
                    bgcolor: theme.palette.secondary.main,
                    color: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: theme.palette.secondary.dark,
                    },
                  }}
                >
                  {t('navbar.getStarted') || 'Get Started'}
                  <ArrowForward sx={{ ml: 1 }} />
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => {
                    const element = document.getElementById('how-it-works');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    borderRadius: 2,
                    borderColor: 'white',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'white',
                      bgcolor: alpha('#ffffff', 0.1),
                    },
                  }}
                >
                  {t('navbar.howItWorks') || 'How It Works'}
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box
                sx={{
                  overflow: 'hidden',
                  width: '100%',
                  height: { xs: '300px', md: '400px' },
                  position: 'relative',
                }}
              >
                <img
                  src="/business-education3.jpg"
                  alt="Business Education"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box id="benefits" sx={{ py: { xs: 6, md: 10 }, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: theme.palette.primary.main,
              mb: 6,
              fontSize: { xs: '2rem', md: '2.5rem' },
            }}
          >
            {t('business.benefitsTitle') || 'Why Choose Astegne for Your Business?'}
          </Typography>
          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    textAlign: 'center',
                    borderRadius: 3,
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <Box
                    sx={{
                      color: theme.palette.primary.main,
                      mb: 2,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    {benefit.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      mb: 2,
                    }}
                  >
                    {benefit.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.8,
                    }}
                  >
                    {benefit.desc}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box id="how-it-works" sx={{ py: { xs: 6, md: 10 }, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: theme.palette.primary.main,
              mb: 8,
              fontSize: { xs: '2rem', md: '2.5rem' },
            }}
          >
            {t('navbar.howItWorks') || 'How It Works'}
          </Typography>
          <Grid container spacing={4}>
            {steps.map((step, index) => (
              <Grid item xs={12} sm={6} md={2.4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    height: '100%',
                    textAlign: 'center',
                    borderRadius: 3,
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                    bgcolor: 'white',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '4px',
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      transform: 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.3s ease',
                    },
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
                      borderColor: theme.palette.primary.main,
                      '&::before': {
                        transform: 'scaleX(1)',
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      fontSize: '2.2rem',
                      fontWeight: 800,
                      color: alpha(theme.palette.primary.main, 0.08),
                      lineHeight: 1,
                    }}
                  >
                    {step.step}
                  </Box>
                  <Box
                    sx={{
                      color: theme.palette.primary.main,
                      mb: 2,
                      mt: 1,
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    {step.icon}
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      mb: 1.5,
                      fontSize: { xs: '1.1rem', md: '1.2rem' },
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: { xs: '0.9rem', md: '0.95rem' },
                      lineHeight: 1.7,
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    {step.desc}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          color: 'white',
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            {t('business.ctaTitle') || 'Ready to Get Started?'}
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
            {t('business.ctaSubtitle') || 'Join Astegne today and provide your employees with access to quality education.'}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/signup/business')}
            sx={{
              px: 6,
              py: 1.5,
              fontSize: '1.2rem',
              fontWeight: 700,
              borderRadius: 2,
              bgcolor: theme.palette.secondary.main,
              color: theme.palette.primary.main,
              '&:hover': {
                bgcolor: theme.palette.secondary.dark,
              },
            }}
          >
            {t('navbar.getStarted') || 'Get Started'}
            <ArrowForward sx={{ ml: 1 }} />
          </Button>
        </Container>
      </Box>
    </Box>
  );
}

export default Business;
